import {useEffect, useState, useCallback} from "react";
import {collection, getDocs, doc, updateDoc, serverTimestamp} from "firebase/firestore";
import {useFirebaseState} from "./firebaseContext";

const getOrderTimestampMs = (order) => {
    const ca = order?.createdAt;
    if (!ca) return 0;
    if (typeof ca.toMillis === "function") return ca.toMillis();
    if (typeof ca.toDate === "function") return ca.toDate().getTime();
    if (typeof ca.seconds === "number") return ca.seconds * 1000;
    if (ca instanceof Date) return ca.getTime();
    const parsed = new Date(ca).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
};

export const useBookOrders = ({forceRefresh} = {}) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    let db = null;
    try {
        const firebaseState = useFirebaseState();
        db = firebaseState?.db || null;
    } catch {
        db = null;
    }

    const fetchOrders = useCallback(async () => {
        if (!db) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const ordersRef = collection(db, "orders");
            const snapshot = await getDocs(ordersRef);

            const items = [];
            if (snapshot && !snapshot.empty) {
                snapshot.forEach((docSnap) => {
                    items.push({id: docSnap.id, ...docSnap.data()});
                });
            }

            items.sort((a, b) => getOrderTimestampMs(b) - getOrderTimestampMs(a));
            setOrders(items);
        } catch (err) {
            setError(err);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, [db]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders, forceRefresh]);

    const updateOrderStatus = useCallback(
        async (orderId, newStatus) => {
            if (!db || !orderId) return;

            const orderRef = doc(db, "orders", orderId);
            await updateDoc(orderRef, {
                status: newStatus,
                updatedAt: serverTimestamp(),
            });

            setOrders((prev) =>
                prev.map((ord) =>
                    ord.id === orderId
                        ? {...ord, status: newStatus, updatedAt: new Date()}
                        : ord
                )
            );
        },
        [db]
    );

    return {
        orders,
        loading,
        error,
        refresh: fetchOrders,
        updateOrderStatus,
    };
};
